import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, BookOpen } from 'lucide-react';

const publications = [
  {
    id: 1,
    title: "Navigating the New RBI Guidelines for Digital Lending",
    category: "Banking & Fintech",
    date: "October 15, 2025",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80",
    excerpt: "An in-depth analysis of the latest regulatory frameworks impacting fintech startups and NBFCs operating in the digital space."
  },
  {
    id: 2,
    title: "Cross-Border M&A: Strategic Compliance Checklists",
    category: "Corporate Law",
    date: "November 02, 2025",
    image: "https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&q=80",
    excerpt: "Key considerations for global capability centers mitigating risks during complex international mergers and acquisitions."
  },
  {
    id: 3,
    title: "Data Privacy & MSMEs: Adapting to the DPDP Act",
    category: "Compliance",
    date: "December 12, 2025",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80",
    excerpt: "A practical guide for business houses and MSMEs to align their data processing practices with the new statutory requirements."
  }
];

const Resources = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
    }
  };

  return (
    <section className="bg-[#eaf2f8] py-32 px-8 md:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="h-[1px] w-12 bg-[#0c3754]/20" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#0c3754] font-bold">
                Knowledge Hub
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-[#0c3754] leading-tight">
              Expert Insights & <span className="italic opacity-50">Publications</span>
            </h2>
          </motion.div>

          <motion.button 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            whileHover={{ gap: "1rem" }}
            className="flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] font-bold text-[#0c3754] border-b border-[#0c3754]/20 pb-2 group"
          >
            <span>View All Articles</span>
            <ArrowRight size={16} className="text-[#0c3754] group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {publications.map((post) => (
            <motion.div 
              key={post.id}
              variants={itemVariants}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-[0_20px_40px_-20px_rgba(12,55,84,0.15)] transition-all duration-500"
            >
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover scale-100 group-hover:scale-110 transition-transform duration-700 ease-in-out grayscale-[30%]"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full flex items-center gap-2">
                  <BookOpen size={12} className="text-[#0c3754]" />
                  <span className="text-[9px] uppercase tracking-widest font-bold text-[#0c3754]">
                    {post.category}
                  </span>
                </div>
              </div>
              
              <div className="p-8">
                <div className="flex items-center gap-2 mb-4 text-[#0c3754]/50">
                  <Calendar size={12} />
                  <span className="text-[10px] uppercase tracking-widest font-semibold">
                    {post.date}
                  </span>
                </div>
                
                <h3 className="text-xl font-serif text-[#0c3754] mb-4 leading-snug group-hover:text-blue-900 transition-colors">
                  {post.title}
                </h3>
                
                <p className="text-sm text-slate-500 leading-relaxed mb-8 line-clamp-3">
                  {post.excerpt}
                </p>
                
                <div className="w-full h-[1px] bg-slate-100 mb-6 group-hover:bg-[#0c3754]/10 transition-colors" />
                
                <button className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#0c3754] group/btn">
                  Read Article
                  <ArrowRight size={14} className="group-hover/btn:translate-x-2 transition-transform duration-300" />
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default Resources;